import React, { useState, useEffect, useCallback } from 'react';
import AuthedAvatarImage from './AuthedAvatarImage';
import { LogOut, LayoutDashboard, DollarSign, ShoppingCart, Settings, MessageSquare, Zap, Network, Bell, BellOff, BarChart3, Coins, Wrench, Database, Users, ShieldAlert } from 'lucide-react';
import FlightModeButton from './FlightModeButton';
import AdminDashboard from './AdminDashboard';
import AdminCustomers from './AdminCustomers';
import AdminTransactions from './AdminTransactions';
import AdminSettings from './AdminSettings';
import AdminOrders from './AdminOrders';
import AdminPriorities from './AdminPriorities';
import ManageIPPanel from './ManageIPPanel';
import AnalyticsPanel from './AnalyticsPanel';
import EVCEditor from './EVCEditor';
import LiveLoginFeed from './LiveLoginFeed';
import TicketsList from './TicketsList';
import ToolsPage from './tools/ToolsPage';
import AdminPriceCatalog from './AdminExcelCatalog';
import SecurityCenter from './security/SecurityCenter';
import { PriceMultiplierPill } from './PriceMultiplierEditor';
import StepUpModal from './StepUpModal';
import { Toaster } from 'sonner';
import useAdminLoginFeed from '../hooks/useAdminLoginFeed';
import useBadges from '../hooks/useBadges';
import useUnreadTracker from '../hooks/useUnreadTracker';
import useDocumentTitle, { PAGE_TITLES } from '../hooks/useDocumentTitle';
import { UnreadBadgeProvider } from '../context/UnreadBadgeContext';
import ServerClock from './ServerClock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel({ user, token, onLogout, refreshUser }) {
  const [activePage, setActivePage] = useState('dashboard');
  const [unreadTotal, setUnreadTotal] = useState(0);
  // Drive the browser tab title — `<Page> (unread) | AUTO-REMAP`.
  // Only the Tickets page gets the unread badge in the title; on
  // every other page the count is irrelevant to the operator's
  // focus and would just noise up the title bar.
  useDocumentTitle(
    PAGE_TITLES[activePage] || 'Admin',
    { count: activePage === 'tickets' ? unreadTotal : 0 },
  );
  // Whether the optional "Tools" nav item is visible — controlled by
  // `service_flags.tools_enabled`. Polled lightly on mount + every 60s so
  // toggling the flag in Settings reflects everywhere without a refresh.
  const [toolsEnabled, setToolsEnabled] = useState(false);
  // Tracks `service_flags.catalog_source`. When `excel_legacy`, we
  // show a persistent amber banner above the page content so the
  // admin can't forget that customer reads are running off the old
  // Excel data and any Price Catalog edits will be invisible to them.
  const [catalogSource, setCatalogSource] = useState('supabase');
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/service/flags`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) {
          setToolsEnabled(!!j.tools_enabled);
          setCatalogSource(j.catalog_source || 'supabase');
        }
      } catch (_) { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    // Subscribe to interactive flips dispatched by the Settings tab so the
    // banner reflects the new value without waiting for the next poll.
    const onSourceChange = (e) => {
      const v = e?.detail?.catalog_source;
      if (v && !cancelled) setCatalogSource(v);
    };
    window.addEventListener('catalog-source-changed', onSourceChange);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('catalog-source-changed', onSourceChange);
    };
  }, []);
  // If the flag flips OFF while we're on the Tools page, bounce back to
  // the dashboard so the admin doesn't get stuck on an orphaned route.
  useEffect(() => {
    if (activePage === 'tools' && !toolsEnabled) setActivePage('dashboard');
  }, [activePage, toolsEnabled]);

  // URL sync — admin uses path-based routes like `/admin`,
  // `/admin/orders`, `/admin/customers` etc. so the address bar
  // reflects the current section and browser back/forward + bookmarks
  // + copy-paste all work as an operator would expect.
  const ADMIN_SECTIONS = React.useMemo(() => ({
    // pathname slug → activePage id
    '':               'dashboard',
    'dashboard':      'dashboard',
    'orders':         'orders',
    'customers':      'customers',
    'tickets':        'tickets',
    'transactions':   'transactions',
    'security':       'security',
    'evc':            'evc',
    'priorities':     'priorities',
    'ip':             'manage-ip',
    'analytics':      'analytics',
    'settings':       'settings',
    'price-catalog':  'price-catalog',
    'tools':          'tools',
  }), []);
  const ID_TO_SLUG = React.useMemo(() => (
    Object.fromEntries(Object.entries(ADMIN_SECTIONS).filter(([k]) => k).map(([k, v]) => [v, k]))
  ), [ADMIN_SECTIONS]);

  // Read pathname on first mount → set activePage.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // /admin/<slug>  or  /admin  or  /admin/  or  /admin/orders?orderId=...
    const parts = window.location.pathname.replace(/^\/+/, '').split('/');
    // parts[0] should be 'admin'; parts[1] is the section slug (may be undefined)
    if (parts[0] === 'admin') {
      const slug = (parts[1] || '').toLowerCase();
      const target = ADMIN_SECTIONS[slug];
      if (target) setActivePage(target);
    }
    // Deep-link an order drawer via `?orderId=<id>` on /admin/orders
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('orderId');
    if (orderId) {
      setOrdersInitialOpenId(orderId);
      setActivePage('orders');
    }
  }, [ADMIN_SECTIONS]);

  // On every activePage change push a new URL — `/admin` for the
  // default dashboard, `/admin/<slug>` for anything else.
  const firstAdminUrlSyncRef = React.useRef(true);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (firstAdminUrlSyncRef.current) {
      firstAdminUrlSyncRef.current = false;
      return;
    }
    const slug = ID_TO_SLUG[activePage];
    const nextPath = slug ? `/admin/${slug}` : '/admin';
    // Preserve query string (e.g. ?orderId=…) only when it targets
    // the same section — otherwise drop it.
    const url = new URL(window.location.href);
    const currentPath = url.pathname;
    if (currentPath === nextPath && url.search) {
      // already in sync, no push
      return;
    }
    url.pathname = nextPath;
    // Drop transient deep-link params when switching sections.
    url.searchParams.delete('orderId');
    window.history.pushState({ adminPage: activePage }, '', url.toString());
  }, [activePage, ID_TO_SLUG]);

  // Browser back/forward → re-read the pathname and sync activePage.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      const parts = window.location.pathname.replace(/^\/+/, '').split('/');
      if (parts[0] !== 'admin') return;
      const slug = (parts[1] || '').toLowerCase();
      const target = ADMIN_SECTIONS[slug];
      if (target) {
        firstAdminUrlSyncRef.current = true; // skip echo pushState
        setActivePage(target);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [ADMIN_SECTIONS]);

  // Granular per-item unread tracking for Orders & Transactions.
  // Each tracker maintains a Set of "seen" IDs in localStorage. Badge
  // count = current_ids_from_server.length - seen_ids.length. A click
  // on a specific row decrements the badge by exactly 1, never wipes it.
  //
  // The ID lists no longer come from dedicated endpoints: `useBadges`
  // fetches every counter in ONE request and keeps it fresh via the
  // `badges:changed` socket push, so the trackers below do zero network
  // I/O — they just re-apply the seen-set rule to the latest payload.
  const { badges, version: badgesVersion } = useBadges({ token, isAdmin: true });
  const badgesRef = React.useRef(null);
  badgesRef.current = badges;
  const ordersFetcher = useCallback(async () => (
    badgesRef.current ? badgesRef.current.order_ids || [] : null
  ), []);
  const txFetcher = useCallback(async () => (
    badgesRef.current ? badgesRef.current.transaction_ids || [] : null
  ), []);
  const ordersTracker = useUnreadTracker({
    storageKey: 'auto-remap.admin.unread.orders',
    fetcher: ordersFetcher,
    enabled: !!token,
    intervalMs: 0,
    refreshSignal: badgesVersion,
  });
  const txTracker = useUnreadTracker({
    storageKey: 'auto-remap.admin.unread.transactions',
    fetcher: txFetcher,
    enabled: !!token,
    intervalMs: 0,
    refreshSignal: badgesVersion,
  });

  // Socket events still drive the live login feed / toasts; the badge
  // counters themselves are refreshed by `useBadges` (same socket).
  useAdminLoginFeed({ token, enabled: true });

  // Transactions are bulk-cleared on section visit (per-row decrement
  // doesn't make sense for a long ledger — admin glances over the whole
  // list at once). Orders/Tickets keep per-item decrement because each
  // entry there is an actionable work item. When admin opens the
  // Transactions tab we mark every currently-known unread tx ID as seen
  // in one shot → badge drops to 0 immediately and stays there.
  useEffect(() => {
    if (activePage !== 'transactions') return;
    if (txTracker.unreadIds.length === 0) return;
    txTracker.markManySeen(txTracker.unreadIds);
  }, [activePage, txTracker]);
  // Live-feed mute state. Stored in localStorage so the floating bell and
  // the header toggle stay in sync across reloads. Default ON for new admins.
  const LS_FEED = 'auto-remap.adminLiveFeed.enabled';
  const [feedEnabled, setFeedEnabled] = useState(() => {
    try { const v = window.localStorage.getItem(LS_FEED); return v === null ? true : v === '1'; }
    catch (_) { return true; }
  });
  const toggleFeed = () => {
    setFeedEnabled((v) => {
      const next = !v;
      try { window.localStorage.setItem(LS_FEED, next ? '1' : '0'); } catch (_) { /* noop */ }
      return next;
    });
  };
  // Cross-tab sync — if the admin clicks the floating bell in LiveLoginFeed,
  // the header toggle reflects it instantly. `storage` event fires across
  // tabs; for same-tab sync we re-read on focus.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS_FEED) setFeedEnabled(e.newValue === '1');
    };
    const onFocus = () => {
      try { setFeedEnabled(window.localStorage.getItem(LS_FEED) !== '0'); } catch (_) { /* noop */ }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);
  // Optional pre-applied filter when we jump into a page from a stat card
  // on the dashboard (e.g. "Open Orders" card → orders page with
  // status = all-non-completed). Wiped on the next manual page change.
  const [ordersInitialStatus, setOrdersInitialStatus] = useState('');
  // Smart Vehicle quick-filter — Dashboard "Smart Vehicle pending" card
  // sets this to 'pending' so the Orders page lands directly on the
  // pending-promotion list.
  const [ordersInitialSmart, setOrdersInitialSmart] = useState('');
  // ECU-finder-only filter — Dashboard / Analytics "ECU Finder" KPI tile
  // sets this to true so the Orders page lands directly on the
  // orders placed via the inline Find-by-ECU smart selector.
  const [ordersInitialEcuFinderOnly, setOrdersInitialEcuFinderOnly] = useState(false);
  // When transactions/customer-drawer asks to open a specific order, we
  // jump to the Orders page and pre-select that ID via this state — the
  // OrdersTable's effect on `initialOpenOrderId` opens the drawer.
  const [ordersInitialOpenId, setOrdersInitialOpenId] = useState('');
  // Rich deep-link filters coming from the Analytics page (charts + KPIs).
  const [ordersInitialFilters, setOrdersInitialFilters] = useState(null);
  const [txInitialFilters, setTxInitialFilters] = useState(null);
  const [txInitialTab, setTxInitialTab] = useState('');

  // Listen for cross-component open-order requests (e.g. from
  // CustomerProfileDrawer's transactions tab). Switches to Orders page
  // and primes the order to open.
  useEffect(() => {
    const handler = (e) => {
      const orderId = e?.detail?.orderId;
      if (!orderId) return;
      setOrdersInitialOpenId(orderId);
      setActivePage('orders');
    };
    window.addEventListener('admin:open-order', handler);
    return () => window.removeEventListener('admin:open-order', handler);
  }, []);
  const navigateWithFilter = (page, opts = {}) => {
    setOrdersInitialStatus(opts.ordersStatus || '');
    setOrdersInitialSmart(opts.ordersSmart || '');
    setOrdersInitialEcuFinderOnly(!!opts.ecuFinderOnly);
    // Rich deep-links from Analytics: an arbitrary filter object for the
    // Orders table / Transactions list. `_nonce` makes a repeated click on
    // the same tile re-apply the filter (object identity changes).
    if (opts.orders) setOrdersInitialFilters({ ...opts.orders, _nonce: Date.now() });
    if (opts.transactions) setTxInitialFilters({ ...opts.transactions, _nonce: Date.now() });
    setTxInitialTab(opts.txTab || '');
    setActivePage(page);
  };

  // Menu click: wipe any pre-applied filter so clicking "Orders" in the
  // top nav always shows the unfiltered list. The filter only lingers
  // when the user arrived via a Dashboard stat tile (navigateWithFilter).
  // NOTE: We intentionally DO NOT clear unread badges here — the user
  // explicitly requested that visiting a section must not zero the badge.
  // Badges decrement only when a specific item is opened (markSeen).
  const navigateFromMenu = (page) => {
    setOrdersInitialStatus('');
    setOrdersInitialSmart('');
    setOrdersInitialEcuFinderOnly(false);
    setOrdersInitialFilters(null);
    setTxInitialFilters(null);
    setTxInitialTab('');
    setActivePage(page);
  };

  // Ticket + recharge-review counters come straight from the unified
  // badge payload — no dedicated polls any more.
  const unreadTotalFromBadges = badges?.tickets_unread ?? 0;
  const pendingReviews = badges?.recharge_reviews_pending ?? 0;
  useEffect(() => { setUnreadTotal(unreadTotalFromBadges); }, [unreadTotalFromBadges]);

  const menuItems = [
    { id: 'dashboard',    label: 'Dashboard',    icon: LayoutDashboard },
    { id: 'orders',       label: 'Orders',       icon: ShoppingCart, badge: ordersTracker.unreadCount },
    { id: 'customers',    label: 'Customers',    icon: Users },
    { id: 'tickets',      label: 'Tickets',      icon: MessageSquare, badge: unreadTotal },
    { id: 'transactions', label: 'Transactions', icon: DollarSign, badge: txTracker.unreadCount },
    { id: 'security',     label: 'Security',     icon: ShieldAlert, badge: pendingReviews },
    { id: 'evc',          label: 'EVC',          icon: Coins },
    { id: 'priorities',   label: 'Priorities',   icon: Zap },
    { id: 'analytics',    label: 'Analytics',    icon: BarChart3 },
    { id: 'settings',     label: 'Settings',     icon: Settings },
    { id: 'price-catalog', label: 'Price Catalog', icon: Database },
    ...(toolsEnabled ? [{ id: 'tools', label: 'Tools', icon: Wrench }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Global step-up MFA modal — listens for `step-up:required`
          custom events from any admin button using `fetchWithStepUp`.
          MUST be mounted here (admin route bypasses App.js entirely),
          else the modal never appears for admin-only sensitive actions
          and the calling fetch hangs forever. */}
      <StepUpModal />
      {/* Sonner Toaster — admin route bypasses App.js so the global
          Toaster mount over there never renders for admin views.
          Without this <Toaster/> here, every `toast.success/error/info`
          call from admin components is a no-op (the toast library
          dispatches the event but there is no surface to display it),
          which makes failures look like "nothing happens" — most
          notably the step-up MFA flow on Daily catch-up. */}
      <Toaster position="top-right" richColors closeButton expand />
      <div className="sticky top-0 z-50 bg-gradient-to-r from-cyan-600 to-blue-700 text-white px-3 sm:px-4 py-3 sm:py-4 shadow-lg">
        <div className="max-w-[88rem] mx-auto flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src="/logo.png"
              alt="AUTO-REMAP"
              width="479"
              height="102"
              className="h-8 sm:h-10 w-auto max-w-[96px] sm:max-w-[180px] rounded-lg bg-white px-1.5 sm:px-2 py-1 shadow-md object-contain flex-shrink-0"
              data-testid="admin-header-logo"
            />
            <h1 className="hidden sm:inline text-sm sm:text-xl font-bold tracking-tight truncate">
              <span className="text-cyan-100">Admin</span>
            </h1>
            <span className="hidden sm:inline-block px-2.5 py-1 bg-white/20 rounded-full text-[10px] sm:text-xs font-bold">ADMIN</span>
            {/* The legacy "Catalog Source" badge that toggled between
                Supabase and Excel modes is gone — Excel is the only
                catalog source now, so the badge has no information to
                surface and was just visual noise in the header. */}
          </div>
          {/* Server clock — live GMT+3 pill, centred in the top-bar
              free space (variant 1a from ТЗ). Auto-syncs every 5 min. */}
          <ServerClock testid="admin-server-clock" />
          <div className="flex items-center gap-2 sm:gap-4">
            <PriceMultiplierPill token={token} />
            <FlightModeButton token={token} />
            <button
              type="button"
              onClick={toggleFeed}
              data-testid="admin-feed-toggle-header"
              title={feedEnabled ? 'Mute live login feed' : 'Resume live login feed'}
              className={`p-2 rounded-lg transition-colors ${feedEnabled ? 'bg-white/25 hover:bg-white/35 text-white' : 'bg-white/10 hover:bg-white/20 text-cyan-100'}`}
            >
              {feedEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            </button>
            <span className="hidden lg:inline text-sm truncate max-w-[200px]">{user.email}</span>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              title="Logout"
              className="flex items-center gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-xs sm:text-sm">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
            {/* Admin profile photo (Phase C) — click jumps to Settings →
                My account → Profile photo. Initials fallback when unset. */}
            <button
              onClick={() => setActivePage('settings')}
              title={user.email}
              data-testid="admin-header-avatar"
              className="ml-1 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/15 hover:bg-white/30 ring-2 ring-white/40 overflow-hidden flex items-center justify-center text-white text-xs font-bold transition-colors flex-shrink-0"
            >
              {user.avatar_url ? (
                <AuthedAvatarImage
                  token={token}
                  src={user.avatar_url}
                  alt="Admin"
                  className="w-full h-full object-cover"
                  fallback={<span>{((user.first_name?.[0] || '') + (user.surname?.[0] || user.email?.[0] || 'A')).toUpperCase()}</span>}
                  testid="admin-header-avatar-img"
                />
              ) : (
                <span>{((user.first_name?.[0] || '') + (user.surname?.[0] || user.email?.[0] || 'A')).toUpperCase()}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Primary navigation — horizontal scroll tabs on every size (no
          hamburger). Active tab is highlighted with the brand gradient so
          it stays obvious on small screens. The wrapping `.edge-fade-x`
          gives a soft right-edge hint when there's more to scroll. */}
      <div className="sticky top-[58px] sm:top-[68px] z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[88rem] mx-auto">
          <div className="edge-fade-x">
            <div className="flex items-center gap-1 p-2 overflow-x-auto no-scrollbar snap-x snap-mandatory">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = activePage === item.id;
                return (
                  <button
                    key={item.id}
                    data-testid={`admin-nav-${item.id}`}
                    onClick={() => navigateFromMenu(item.id)}
                    className={`relative snap-start flex items-center gap-1.5 px-2.5 lg:px-3 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm flex-shrink-0 ${active ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    <Icon className="w-4 h-4 lg:w-[18px] lg:h-[18px]" />
                    {item.label}
                    {item.badge > 0 && (
                      <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${active ? 'bg-white text-cyan-700' : 'bg-red-500 text-white animate-pulse'}`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full">
        {/* Catalog Source kill-switch banner — shown only when admin
            flipped to legacy Excel mode. Persistent across all admin
            pages so the operator can't forget that Price Catalog
            edits are detached from customer reads. */}
        {/* Excel-legacy banner intentionally removed — after the
            Supabase decommission `excel_legacy` is the ONLY mode, so
            telling the admin "Excel mode is on" is just noise. */}
        <UnreadBadgeProvider value={{
          markOrderSeen: ordersTracker.markSeen,
          markTransactionSeen: txTracker.markSeen,
          markTicketSeen: () => {},
          unreadOrderIds: new Set(ordersTracker.unreadIds),
          unreadTransactionIds: new Set(txTracker.unreadIds),
        }}>
        {activePage === 'dashboard' && (<div className="max-w-[88rem] mx-auto"><AdminDashboard token={token} setActivePage={setActivePage} navigateWithFilter={navigateWithFilter} /></div>)}
        {activePage === 'customers' && <AdminCustomers token={token} />}
        {activePage === 'transactions' && (
          <AdminTransactions
            token={token}
            initialFilters={txInitialFilters}
            initialTab={txInitialTab}
            onOpenOrder={(orderId) => {
              // Cross-section nav: jump to Orders tab and auto-open
              // the drawer for the txn's parent order. Mirrors the
              // pattern used by the Dashboard's `navigateWithFilter`.
              setOrdersInitialOpenId(orderId);
              setActivePage('orders');
            }}
          />
        )}
        {activePage === 'security' && <SecurityCenter token={token} />}
        {activePage === 'orders' && <AdminOrders token={token} initialStatus={ordersInitialStatus} initialSmart={ordersInitialSmart} initialEcuFinderOnly={ordersInitialEcuFinderOnly} initialFilters={ordersInitialFilters} initialOpenOrderId={ordersInitialOpenId} onOpenedOrderConsumed={() => setOrdersInitialOpenId('')} />}
        {activePage === 'priorities' && <AdminPriorities token={token} />}
        {activePage === 'tickets' && <TicketsList token={token} isAdmin={true} />}
        {activePage === 'manage-ip' && <ManageIPPanel token={token} />}
        {activePage === 'analytics' && <AnalyticsPanel token={token} go={navigateWithFilter} />}
        {activePage === 'evc' && <EVCEditor token={token} />}
        {activePage === 'settings' && <AdminSettings token={token} onNavigate={navigateFromMenu} onProfileChanged={refreshUser} />}
        {activePage === 'price-catalog' && <AdminPriceCatalog token={token} />}
        {activePage === 'tools' && toolsEnabled && <ToolsPage />}
        </UnreadBadgeProvider>
      </div>

      {/* Real-time login feed — top-right toast stack + floating bell.
          The header bell button and the floating one share state via
          localStorage so toggling either mutes both. */}
      <LiveLoginFeed token={token} />
    </div>
  );
}
